import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

interface TeamMember {
  id: string;
  full_name: string;
  role: string;
  plan: string;
  created_at: string;
}

const ROLE_LABELS: Record<string, { label: string; color: string }> = {
  admin: { label: 'Admin', color: 'bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-400' },
  user: { label: 'Usuario', color: 'bg-secondary-100 text-secondary-700 dark:bg-secondary-500/10 dark:text-secondary-400' },
  manager: { label: 'Gerente', color: 'bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400' },
  operator: { label: 'Operador', color: 'bg-sky-100 text-sky-700 dark:bg-sky-500/10 dark:text-sky-400' },
};

const PLAN_LABELS: Record<string, string> = {
  trial: 'Trial',
  starter: 'Starter',
  professional: 'Professional',
  business: 'Business',
  enterprise: 'Enterprise',
};

export default function SettingsUsersPage() {
  const { profile } = useAuth();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Add user modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<TeamMember[]>([]);
  const [searching, setSearching] = useState(false);
  const [addingId, setAddingId] = useState<string | null>(null);

  // Edit role
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editRole, setEditRole] = useState('');
  const [savingRole, setSavingRole] = useState(false);

  const fetchMembers = useCallback(async () => {
    if (!profile?.company_id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data, error: fetchError } = await supabase
        .from('profiles')
        .select('id, full_name, role, plan, created_at')
        .eq('company_id', profile.company_id)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setMembers((data || []) as TeamMember[]);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar usuarios');
    } finally {
      setLoading(false);
    }
  }, [profile?.company_id]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  // Search users to add
  const handleSearch = async () => {
    if (!searchTerm.trim() || searchTerm.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const { data } = await supabase
        .from('profiles')
        .select('id, full_name, role, plan, created_at')
        .ilike('full_name', `%${searchTerm.trim()}%`)
        .limit(10);

      // Filter out users already in the company
      const existingIds = new Set(members.map((m) => m.id));
      const filtered = ((data || []) as TeamMember[]).filter((u) => !existingIds.has(u.id));
      setSearchResults(filtered);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleAddUser = async (userId: string) => {
    if (!profile?.company_id) return;
    setAddingId(userId);
    try {
      const { error: updateErr } = await supabase
        .from('profiles')
        .update({ company_id: profile.company_id, updated_at: new Date().toISOString() })
        .eq('id', userId);

      if (updateErr) throw updateErr;

      setSuccessMsg('Usuario adicionado a equipe com sucesso!');
      setShowAddModal(false);
      setSearchTerm('');
      setSearchResults([]);
      fetchMembers();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao adicionar usuario');
    } finally {
      setAddingId(null);
      setTimeout(() => setSuccessMsg(''), 3000);
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleRemoveUser = async (userId: string) => {
    if (!window.confirm('Tem certeza que deseja remover este usuario da empresa?')) return;
    try {
      const { error: updateErr } = await supabase
        .from('profiles')
        .update({ company_id: null, updated_at: new Date().toISOString() })
        .eq('id', userId);

      if (updateErr) throw updateErr;

      setSuccessMsg('Usuario removido da equipe.');
      fetchMembers();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao remover usuario');
    } finally {
      setTimeout(() => setSuccessMsg(''), 3000);
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleUpdateRole = async (userId: string) => {
    if (!editRole) return;
    setSavingRole(true);
    try {
      const { error: updateErr } = await supabase
        .from('profiles')
        .update({ role: editRole, updated_at: new Date().toISOString() })
        .eq('id', userId);

      if (updateErr) throw updateErr;

      setSuccessMsg('Cargo atualizado com sucesso!');
      setEditingId(null);
      fetchMembers();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao atualizar cargo');
    } finally {
      setSavingRole(false);
      setTimeout(() => setSuccessMsg(''), 3000);
      setTimeout(() => setError(''), 3000);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const canManage = profile?.role === 'admin' || profile?.role === 'manager';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-heading font-bold text-foreground-950">Usuarios da Empresa</h2>
          <p className="text-sm text-foreground-500 mt-1">
            Gerencie membros da equipe, permissoes e acesso ao painel
          </p>
        </div>
        {canManage && (
          <button
            onClick={() => { setShowAddModal(true); setSearchTerm(''); setSearchResults([]); }}
            className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
          >
            <i className="ri-add-line" /> Adicionar Usuario
          </button>
        )}
      </div>

      {successMsg && (
        <div className="bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 rounded-xl px-4 py-3 flex items-center gap-3">
          <i className="ri-check-line text-emerald-500 text-lg" />
          <p className="text-sm text-emerald-700 dark:text-emerald-400">{successMsg}</p>
        </div>
      )}
      {error && (
        <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl px-4 py-3 flex items-center gap-3">
          <i className="ri-error-warning-line text-red-500 text-lg" />
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : members.length === 0 ? (
          <div className="text-center py-16 px-6">
            <div className="w-14 h-14 rounded-full bg-background-100 dark:bg-background-200 flex items-center justify-center mx-auto mb-4">
              <i className="ri-team-line text-2xl text-foreground-400" />
            </div>
            <p className="text-sm font-medium text-foreground-700 dark:text-foreground-300 mb-1">
              Nenhum usuario na equipe
            </p>
            <p className="text-xs text-foreground-500 mb-5">
              Adicione membros da equipe para colaborar no painel
            </p>
            {canManage && (
              <button
                onClick={() => { setShowAddModal(true); setSearchTerm(''); setSearchResults([]); }}
                className="px-5 py-2.5 rounded-xl bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                Adicionar primeiro usuario
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-100 dark:border-background-200">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Usuario</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Cargo</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider hidden sm:table-cell">Plano</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider hidden lg:table-cell">Cadastro</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Acoes</th>
                </tr>
              </thead>
              <tbody>
                {members.map((member) => {
                  const roleInfo = ROLE_LABELS[member.role] || ROLE_LABELS.user;
                  return (
                    <tr
                      key={member.id}
                      className="border-b border-background-50 dark:border-background-200 hover:bg-background-50/60 dark:hover:bg-background-200/50 transition-colors"
                    >
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl bg-background-100 dark:bg-background-200 flex items-center justify-center text-xs font-bold text-foreground-500 dark:text-foreground-400 flex-shrink-0">
                            {(member.full_name || 'U').split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-foreground-900 dark:text-foreground-100 text-[13px]">
                              {member.full_name || 'Sem nome'}
                            </p>
                            {member.id === profile?.id && (
                              <span className="text-[10px] text-primary-500 font-medium">Voce</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        {editingId === member.id && canManage ? (
                          <div className="flex items-center gap-2">
                            <select
                              value={editRole}
                              onChange={(e) => setEditRole(e.target.value)}
                              className="px-2 py-1 bg-white dark:bg-background-100 border border-background-200 rounded-lg text-xs outline-none focus:border-primary-400 cursor-pointer"
                            >
                              {Object.entries(ROLE_LABELS).map(([key, info]) => (
                                <option key={key} value={key}>{info.label}</option>
                              ))}
                            </select>
                            <button
                              onClick={() => handleUpdateRole(member.id)}
                              disabled={savingRole}
                              className="w-7 h-7 rounded-lg bg-primary-50 text-primary-600 hover:bg-primary-100 flex items-center justify-center cursor-pointer transition-colors"
                            >
                              <i className="ri-check-line text-sm" />
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              className="w-7 h-7 rounded-lg bg-background-100 text-foreground-500 hover:bg-background-200 flex items-center justify-center cursor-pointer transition-colors"
                            >
                              <i className="ri-close-line text-sm" />
                            </button>
                          </div>
                        ) : (
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium ${roleInfo.color}`}>
                            {roleInfo.label}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 hidden sm:table-cell">
                        <span className="text-xs text-foreground-600 dark:text-foreground-400">
                          {PLAN_LABELS[member.plan] || member.plan}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-foreground-500 text-[13px] hidden lg:table-cell">
                        {formatDate(member.created_at)}
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        {canManage && member.id !== profile?.id && (
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => { setEditingId(member.id); setEditRole(member.role); }}
                              className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:text-primary-400 dark:hover:bg-primary-500/10 transition-colors cursor-pointer"
                              title="Editar cargo"
                            >
                              <i className="ri-edit-line text-base" />
                            </button>
                            <button
                              onClick={() => handleRemoveUser(member.id)}
                              className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-red-600 hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-500/10 transition-colors cursor-pointer"
                              title="Remover da empresa"
                            >
                              <i className="ri-user-unfollow-line text-base" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="px-5 py-3 border-t border-background-100 dark:border-background-200 flex items-center justify-between">
          <p className="text-xs text-foreground-500">
            {members.length} usuario{members.length !== 1 ? 's' : ''} na equipe
          </p>
        </div>
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowAddModal(false)} />
          <div className="relative bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 w-full max-w-[480px] mx-4 p-6 animate-fadeIn">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-heading font-bold text-foreground-950 dark:text-foreground-100">
                Adicionar Usuario a Equipe
              </h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:bg-background-100 hover:text-foreground-600 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-foreground-600 dark:text-foreground-400 mb-2">
                  Buscar por nome
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                    placeholder="Digite o nome do usuario..."
                    className="flex-1 px-3 py-2 bg-white dark:bg-background-100 border border-background-200 dark:border-background-300 rounded-lg text-sm text-foreground-900 dark:text-foreground-100 placeholder-foreground-400 outline-none focus:border-primary-400 transition-all"
                  />
                  <button
                    onClick={handleSearch}
                    disabled={searching}
                    className="px-3 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {searching ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-search-line" />}
                  </button>
                </div>
              </div>

              {searchResults.length > 0 && (
                <div className="space-y-2 max-h-[240px] overflow-y-auto">
                  {searchResults.map((user) => {
                    const roleInfo = ROLE_LABELS[user.role] || ROLE_LABELS.user;
                    return (
                      <div
                        key={user.id}
                        className="flex items-center justify-between p-3 rounded-xl border border-background-200 hover:border-primary-200 transition-colors"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 rounded-xl bg-background-100 dark:bg-background-200 flex items-center justify-center text-xs font-bold text-foreground-500 flex-shrink-0">
                            {(user.full_name || 'U').split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-foreground-900 dark:text-foreground-100 truncate">
                              {user.full_name || 'Sem nome'}
                            </p>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${roleInfo.color}`}>
                              {roleInfo.label}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => handleAddUser(user.id)}
                          disabled={addingId === user.id}
                          className="px-3 py-1.5 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
                        >
                          {addingId === user.id ? 'Adicionando...' : 'Adicionar'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {searchResults.length === 0 && searchTerm.trim().length >= 2 && !searching && (
                <p className="text-sm text-foreground-500 text-center py-4">
                  Nenhum usuario encontrado com esse nome
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}